// src/components/ProjectSprintCard.tsx
import { useMemo } from 'react';
import { useAtom } from 'jotai';
import { sprintsAtom, userAllocationsAtom, projectsAtom } from '@/lib/store';
import { Sprint } from '@/types';
import SprintTarget from './SprintTarget';

// Import the same color array used in other components
const PROJECT_COLORS = [
  'bg-primary', 'bg-green-500', 'bg-yellow-500', 'bg-red-500',
  'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-blue-400',
  'bg-orange-500', 'bg-teal-500', 'bg-cyan-500', 'bg-lime-500'
];

interface ProjectPoints {
  total: number;
  completed: number;
}

interface ProjectData {
  key: string;
  name: string;
  color: string;
  total: number;
  completed: number;
  percentComplete: number;
}

interface SprintProjectData {
  sprint: Sprint;
  projects: ProjectData[];
  totalPoints: number;
  completedPoints: number;
}

export default function ProjectSprintCard() {
  const [sprints] = useAtom(sprintsAtom);
  const [userAllocations] = useAtom(userAllocationsAtom);
  const [projects] = useAtom(projectsAtom);

  // Create a map of project keys to project names
  const projectNameMap = useMemo(() => {
    const map = new Map<string, string>();
    projects.forEach(project => {
      map.set(project.key, project.name);
    });
    return map;
  }, [projects]);

  // Calculate project story points per sprint
  const projectSprintData = useMemo<SprintProjectData[]>(() => {
    if (sprints.length === 0 || userAllocations.length === 0) {
      return [];
    }

    // First collect all unique project keys
    const projectKeys = new Set<string>();
    userAllocations.forEach(allocation => {
      Object.values(allocation.sprints).forEach(sprintData => {
        sprintData.issues.forEach(issue => {
          if (issue.storyPoints) {
            const projectKey = issue.key.split('-')[0];
            projectKeys.add(projectKey);
          }
        });
      });
    });

    // Sort project keys alphabetically
    const sortedProjectKeys = Array.from(projectKeys).sort();
    
    // Create a color map for projects
    const projectColorMap = new Map<string, string>(
      sortedProjectKeys.map((key, index) => [
        key, 
        PROJECT_COLORS[index % PROJECT_COLORS.length]
      ])
    );

    // Calculate story points per project per sprint
    return sprints.map(sprint => {
      const projectPoints = new Map<string, ProjectPoints>();
      
      // Initialize all projects with zero points
      sortedProjectKeys.forEach(projectKey => {
        projectPoints.set(projectKey, { total: 0, completed: 0 });
      });
      
      // Sum up story points for each project in this sprint
      userAllocations.forEach(allocation => {
        const sprintData = allocation.sprints[sprint.id];
        if (sprintData) {
          sprintData.issues.forEach(issue => {
            if (issue.storyPoints) {
              const projectKey = issue.key.split('-')[0];
              const current = projectPoints.get(projectKey) || { total: 0, completed: 0 };
              
              current.total += issue.storyPoints;
              if (issue.done) {
                current.completed += issue.storyPoints;
              }
              
              projectPoints.set(projectKey, current);
            }
          });
        }
      });
      
      // Convert to array and sort by project key (alphabetically)
      const projectsArray = Array.from(projectPoints.entries())
        .map(([key, points]): ProjectData => ({
          key,
          name: projectNameMap.get(key) || key,
          color: projectColorMap.get(key) || 'bg-gray-400',
          total: points.total,
          completed: points.completed,
          percentComplete: points.total > 0 ? Math.round((points.completed / points.total) * 100) : 0
        }))
        .filter(project => project.total > 0)
        .sort((a, b) => a.key.localeCompare(b.key));
      
      return {
        sprint,
        projects: projectsArray,
        totalPoints: projectsArray.reduce((sum, project) => sum + project.total, 0),
        completedPoints: projectsArray.reduce((sum, project) => sum + project.completed, 0)
      };
    });
  }, [sprints, userAllocations, projectNameMap]);

  if (projectSprintData.length === 0) {
    return <></>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 overflow-x-auto">
      {projectSprintData.map(sprintData => (
        <div 
          key={sprintData.sprint.id}
          className="bg-gray-50 dark:bg-gray-700 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
        >
          <div className="bg-primary h-2 w-full"></div>
          <div className="p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold">{sprintData.sprint.name}</h3>
              <div className="text-sm text-gray-500 dark:text-gray-300">
                {sprintData.totalPoints > 0 
                  ? Math.round((sprintData.completedPoints / sprintData.totalPoints) * 100)
                  : 0}% Complete
              </div>
            </div>
            
            <SprintTarget target={sprintData.sprint.target} className="mb-3" />
            
            <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              {new Date(sprintData.sprint.startDate).toLocaleDateString()} - {new Date(sprintData.sprint.endDate).toLocaleDateString()}
            </div>
            
            <div className="mb-3 bg-gray-200 dark:bg-gray-600 rounded-full h-2.5">
              <div 
                className="bg-green-400 h-2.5 rounded-full" 
                style={{ 
                  width: `${sprintData.totalPoints > 0 
                    ? (sprintData.completedPoints / sprintData.totalPoints) * 100 
                    : 0}%` 
                }}
              ></div>
            </div>
            
            <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
              <span className="font-medium">{sprintData.completedPoints}/{sprintData.totalPoints}</span> story points
            </div>
            
            <div className="flex justify-between items-center mb-2">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Projects</h4>
              <span className="text-xs text-gray-500 dark:text-gray-400">{sprintData.projects.length} active projects</span>
            </div>
            
            <div>
              {sprintData.projects.map(project => (
                <div key={project.key} className="flex flex-col mb-3">
                  <div className="flex items-center mb-1">
                    <div className={`w-3 h-3 ${project.color} rounded-sm mr-2 flex-shrink-0 mt-1`}></div>
                    <div className="font-medium truncate mr-2" title={project.name}>
                      <span className="font-bold">{project.key}</span>
                    </div>
                  </div>
                  
                  {project.name !== project.key && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate ml-5 -mt-1 mb-1" title={project.name}>
                      {project.name}
                    </div>
                  )}
                  
                  <div className="flex items-center ml-5">
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-gray-500 dark:text-gray-400">
                          {project.completed}/{project.total} points
                        </span>
                        <span className="text-gray-500 dark:text-gray-400 ml-2">
                          {project.percentComplete}%
                        </span>
                      </div>
                      <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                        <div 
                          className="bg-green-400 h-1.5 rounded-full" 
                          style={{ width: `${project.percentComplete}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}